<script setup lang="ts">
import type { OrdenationDropdownOption, OrdenationDropdownProps } from '.';
import type { ButtonVariants } from '~/components/ui/button';
import type Select from '~/components/ui/select/Select.vue';
import type { ClassValue } from 'clsx';

import { defaultOrdenationOptions } from '~/constants';
import { cn } from '~/utils';

import { ordenationSelectLabelVariant } from '.';

interface Props extends OrdenationDropdownProps {
	isTransitioning?: boolean;
	options?: OrdenationDropdownOption[];
	defaultSortBy?: OrdenationDropdownOption['sortBy'];
	defaultOrderBy?: OrdenationDropdownOption['orderBy'];
	buttonVariant?: ButtonVariants['variant'];
	buttonSize?: ButtonVariants['size'];
	buttonClasses?: ClassValue;
}

const props = withDefaults(defineProps<Props>(), {
	labelVariant: 'default',
	isTransitioning: false,
	defaultSortBy: undefined,
	defaultOrderBy: 'asc',
	options: () => defaultOrdenationOptions,
	buttonVariant: 'secondary',
	buttonSize: 'sm'
});

const emit = defineEmits<{
	(
		e: 'updateSelection',
		payload: {
			sortBy: OrdenationDropdownOption['sortBy'];
			orderBy: OrdenationDropdownOption['orderBy'];
		}
	): void;
	(e: 'reorder-clicked', value: OrdenationDropdownOption['value']): void;
}>();

const sortBy = defineModel<OrdenationDropdownOption['sortBy']>('sortBy');
const orderBy = defineModel<OrdenationDropdownOption['orderBy']>('orderBy');
const modelValue = ref<string | undefined>(props.defaultSortBy);
const label = ref<string | undefined>();

const hasUserInteracted = ref(false);

// Track select state
const open = ref(false);
const pendingOpen = ref(false);
let transitionEndTimestamp = 0;
let transitionProbeTimeout: ReturnType<typeof setTimeout> | null = null;
const gracePeriodMs = 3000; // 3 seconds grace period after transition ends
let openProbeTimeout: ReturnType<typeof setTimeout> | null = null;
let graceDelayTimeout: ReturnType<typeof setTimeout> | null = null;

// Debug logging for Safari
const debugSafari = (_message: string, _data?: any) => {};

// Helper to know if we're inside the grace period window
function isInGracePeriodNow(): boolean {
	const timeSinceTransitionEnd = Date.now() - transitionEndTimestamp;
	return transitionEndTimestamp > 0 && timeSinceTransitionEnd < gracePeriodMs;
}

// Safari-compatible transition handler
function handleTransitionEnd() {
	if (pendingOpen.value) {
		debugSafari('handleTransitionEnd called - OPENING SELECT');
		pendingOpen.value = false;
		open.value = true;
	}
}

// Watch for transition changes with Safari compatibility
watch(
	() => props.isTransitioning,
	(isTransitioning, wasTransitioning) => {
		if (wasTransitioning && !isTransitioning) {
			// Transition just ended - mark timestamp for grace period
			transitionEndTimestamp = Date.now();

			if (pendingOpen.value) {
				// User had clicked during transition
				handleTransitionEnd();
			}
		}
	}
);

// Handle the select open/close state changes from Radix
function handleOpenChange(isOpen: boolean) {
	// Closing always allowed
	if (!isOpen) {
		open.value = false;
		pendingOpen.value = false;
		if (openProbeTimeout) {
			clearTimeout(openProbeTimeout);
			openProbeTimeout = null;
		}
		if (graceDelayTimeout) {
			clearTimeout(graceDelayTimeout);
			graceDelayTimeout = null;
		}
		return;
	}

	// When attempting to open, delay while transitioning or within grace period (all browsers)
	if (props.isTransitioning || isInGracePeriodNow()) {
		pendingOpen.value = true;
		open.value = false;

		// If not transitioning, open after short delay
		if (!props.isTransitioning) {
			if (graceDelayTimeout) {
				clearTimeout(graceDelayTimeout);
			}
			graceDelayTimeout = setTimeout(() => {
				if (pendingOpen.value) handleTransitionEnd();
				graceDelayTimeout = null;
			}, 500);
		}
		return;
	}

	// Not transitioning yet: start a short probe. If no transition starts, open after 150ms
	pendingOpen.value = true;
	open.value = false;
	if (openProbeTimeout) clearTimeout(openProbeTimeout);
	openProbeTimeout = setTimeout(() => {
		if (!props.isTransitioning && pendingOpen.value) {
			handleTransitionEnd();
		}
		openProbeTimeout = null;
	}, 150);
}

function clearOrdenationSelect() {
	modelValue.value = undefined;
	label.value = undefined;
	sortBy.value = undefined;
	orderBy.value = undefined;
	emit('updateSelection', {
		sortBy: undefined,
		orderBy: undefined
	});

	emit('reorder-clicked', props.options[0]?.value);
}

function updateModelValue(value: string) {
	hasUserInteracted.value = true;

	if (
		value === 'default_ordenation' &&
		modelValue.value === 'default_ordenation'
	) {
		clearOrdenationSelect();
		return;
	}

	modelValue.value = value;
	const option = props.options.find(option => option.value === value);

	if (!option) return;
	modelValue.value = option.value;
	label.value = option.label;
	sortBy.value = option.sortBy;
	orderBy.value = option.orderBy;

	emit('updateSelection', {
		sortBy: option.sortBy,
		orderBy: option.orderBy
	});

	emit('reorder-clicked', option.value);
}
watch(
	[sortBy, orderBy],
	([newSortBy, newOrderBy]) => {
		// If both sortBy and orderBy are undefined and we have defaults, use defaults first
		// BUT only if the user hasn't manually interacted with the select
		if (
			!newSortBy &&
			!newOrderBy &&
			!label.value &&
			!hasUserInteracted.value &&
			props.defaultSortBy &&
			props.defaultOrderBy
		) {
			const defaultOption = props.options.find(
				option =>
					option.sortBy === props.defaultSortBy &&
					option.orderBy === props.defaultOrderBy
			);
			if (defaultOption) {
				label.value = defaultOption.label;
				modelValue.value = defaultOption.value;
				return;
			}
		}

		// Find the matching option (including when both are undefined for "Mais Relevantes")
		const currentOption = props.options.find(
			option => option.sortBy === newSortBy && option.orderBy === newOrderBy
		);

		if (currentOption) {
			// If we found a matching option, update the label and modelValue
			// Always update if no label is set, or if the values don't match
			if (!label.value || modelValue.value !== currentOption.value) {
				label.value = currentOption.label;
				modelValue.value = currentOption.value;
			}
		}
	},
	{ immediate: true }
);

// Cleanup on component unmount
onUnmounted(() => {
	if (openProbeTimeout) {
		clearTimeout(openProbeTimeout);
		openProbeTimeout = null;
	}
	if (graceDelayTimeout) {
		clearTimeout(graceDelayTimeout);
		graceDelayTimeout = null;
	}
});
</script>

<template>
	<div
		class="relative flex flex-col items-end"
		:class="{ 'cursor-pointer': isTransitioning }"
	>
		<Select
			:model-value="modelValue"
			data-testid="select-ordenation"
			class="h-[50px] border border-white-dark-4 p-0!"
			:open="open"
			@update:model-value="updateModelValue"
			@update:open="handleOpenChange"
		>
			<SelectTrigger
				class="h-10 w-min border-none p-0 shadow-none focus:border-none focus:ring-0"
				arrow-class="hidden"
			>
				<Button
					:variant="props.buttonVariant"
					:size="props.buttonSize"
					:is-skeleton-loading="false"
					class="h-10 px-3 focus:border-none md:px-4"
					:class="
						cn(
							{
								'w-auto sm:w-[170px] lg:w-auto xl:w-[170px]':
									labelVariant === 'default'
							},
							buttonClasses
						)
					"
				>
					<IconMaterialSymbolsLightSwapVert class="size-5 text-primary" />
					<p :class="ordenationSelectLabelVariant({ variant: labelVariant })">
						{{ label }}
					</p>
				</Button>
			</SelectTrigger>

			<SelectContent
				class="absolute top-full mt-2 min-w-[200px] border border-white-dark-3 bg-white px-0 py-2 shadow-[0_1px_3px_1px_#00000026] md:min-w-[250px]"
				:class="{
					'translate-x-[-155px] md:translate-x-[-80px] lg:translate-x-[-196px] xl:translate-x-[-80px]':
						labelVariant === 'default',
					'translate-x-[-195px] lg:translate-x-[-160px]':
						labelVariant !== 'default'
				}"
			>
				<SelectGroup class="p-0!">
					<SelectItem
						v-for="option in options"
						:key="`ordenation-select-option-${option.value}`"
						:value="option.value!"
						class="cursor-pointer whitespace-nowrap px-4 py-3 text-primary focus:bg-white-dark-1 focus:text-primary"
					>
						{{ option.label }}
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>
	</div>
</template>
